// src/pages/menu/ItemPage.jsx
import React, { useRef, useEffect, useMemo } from "react";
import { Navigate, useParams, Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Quote, Wine, Flame, Crown, MapPin } from "lucide-react";

import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import SEO from "../../components/SEO";

import { getPizzaBySlug, D10, PIZZAS } from "../../data/menu";

const SITE = {
  name: "D10's Pizza Fantasista",
  baseUrl: "https://www.d10s.pizza",
  ogDefault: "/og-preview.png",
};

/* --- HELPER: Get Related Pizzas (excludes current and fritas) --- */
const getRelatedPizzas = (currentId) => {
  const all = Array.isArray(PIZZAS) ? PIZZAS : D10?.pizzas || [];
  return all.filter((p) => {
    // Exclude current pizza
    if (p.id === currentId) return false;
    // Exclude fritas (check slug, name, or category)
    const slug = (p.slug || "").toLowerCase();
    const name = (p.name || "").toLowerCase();
    const category = (p.category || "").toLowerCase();
    if (slug.includes("frit") || name.includes("frit") || category.includes("frit")) return false;
    return true;
  });
};

/* --- 1. HERO SECTION --- */
const ProductHero = ({ pizza }) => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1.1, 1]);

  const heroSrc = pizza?.heroImage || pizza?.detailImage || pizza?.image;

  // map-able fields (with safe defaults)
  const heroBadge = pizza?.badgeLabel || pizza?.seriesLabel || "Signature Series";
  const heroStyle = pizza?.style || "Neapolitan Contemporary";
  const bakeLabel =
    pizza?.bakeLabel ||
    (pizza?.bakeMethod ? `${pizza.bakeMethod}${pizza?.bakeTemp ? ` at ${pizza.bakeTemp}` : ""}` : null) ||
    "Wood Fired at 450°C";

  return (
    <header ref={containerRef} className="relative w-full h-[95vh] bg-[#0F172A] overflow-hidden">
      <div className="absolute inset-0 z-0">
        <motion.img
          style={{ scale: imageScale }}
          src={heroSrc}
          alt={pizza.name}
          className="w-full h-full object-cover opacity-60"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/80 via-transparent to-transparent" />
      </div>

      {/* FIXED: Changed top-0 to top-20 (80px) to position below navbar, and z-50 to z-40 */}
      <div className="absolute top-20 sm:top-24 left-0 w-full z-40 p-6">
        <Link
          to="/menu"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 backdrop-blur-md border border-white/10 text-white hover:bg-[#0087E6] hover:border-[#0087E6] transition-all duration-300 group"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          <span className="font-bold uppercase tracking-widest text-[10px]">Back to Menu</span>
        </Link>
      </div>

      <div className="relative z-10 container mx-auto px-6 h-full flex flex-col justify-center">
        <motion.div style={{ y: textY }} className="max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="px-3 py-1 bg-[#0087E6] text-white text-[10px] font-bold uppercase tracking-widest rounded-sm">
              {heroBadge}
            </span>
            <div className="h-px w-12 bg-white/30" />
            <span className="font-serif italic text-white/70 text-lg">The "{pizza.nickname}"</span>
          </div>

          <h1 className="font-anton text-[12vw] sm:text-[8vw] leading-[0.9] text-white uppercase mb-8">
            {pizza.name}
          </h1>

          <div className="flex flex-col sm:flex-row sm:items-center gap-8 border-t border-white/10 pt-8 mt-8">
            <div>
              <p className="text-[10px] text-[#0087E6] font-bold uppercase tracking-widest mb-1">Price</p>
              <p className="font-anton text-4xl text-white">{pizza.price}</p>
            </div>
            <div className="hidden sm:block w-px h-12 bg-white/10" />
            <div>
              <p className="text-[10px] text-[#0087E6] font-bold uppercase tracking-widest mb-1">Style</p>
              <p className="font-anton text-2xl text-white">{heroStyle}</p>
            </div>
            <div className="hidden sm:block w-px h-12 bg-white/10" />
            <div className="flex items-center gap-2">
              <Flame className="text-[#FFD700]" size={20} />
              <span className="text-white/80 text-sm font-manrope">{bakeLabel}</span>
            </div>
          </div>
        </motion.div>
      </div>
    </header>
  );
};

/* --- 2. THE DISH (Culinary Focus) --- */
const TheDish = ({ pizza }) => {
  const ticker = Array.isArray(pizza?.ticker) && pizza.ticker.length
    ? pizza.ticker
    : [
        { type: "anton", text: "D10 Fantasista" },
        { type: "serif", text: "★ Wood Fired" },
        { type: "antonDark", text: "No Compromise" },
      ];

  const bgWords =
    (Array.isArray(pizza?.bgWords) && pizza.bgWords.length ? pizza.bgWords : ["PASSION", "GLORY", "TASTE"]).slice(
      0,
      3
    );

  const philosophy = pizza?.philosophy || {};
  const philosophyKicker = philosophy.kicker || "The Philosophy";
  const philosophyLines = Array.isArray(philosophy.lines) && philosophy.lines.length
    ? philosophy.lines
    : ["Don't just", "eat it.", "Feel it."];

  // we keep the same layout; only the strings are mapped
  const pLine0 = philosophyLines[0] ?? "Don't just";
  const pLine1 = philosophyLines[1] ?? "eat it.";
  const pLine2 = philosophyLines[2] ?? "Feel it.";

  const kitchenQuote =
    pizza?.kitchenQuote ||
    pizza?.meta?.note ||
    "We don't hide behind sauce. We prove the dough for 48 hours not because it's easier, but because it creates a texture that you remember long after the final whistle.";

  const sensory = pizza?.sensory || {};
  const sensoryTexture = sensory.texture || "Airy & Crisp";
  const sensoryProfile = sensory.profile || "Bold";
  const sensoryFinish = sensory.finish || "Charred";

  return (
    <section className="bg-white relative overflow-hidden flex flex-col justify-center">
      {/* 1. SCROLLING TICKER (The Stadium Banner vibe) */}
      <div className="w-full bg-[#0087E6] text-white overflow-hidden py-3 transform -rotate-1 origin-left scale-105 z-20 shadow-lg border-y-4 border-[#0F172A]">
        <div className="whitespace-nowrap flex animate-marquee gap-8">
          {[...Array(10)].map((_, i) => (
            <React.Fragment key={i}>
              {ticker.map((t, idx) => {
                if (t.type === "serif") {
                  return (
                    <span key={idx} className="font-serif italic text-2xl">
                      {t.text}
                    </span>
                  );
                }
                if (t.type === "antonDark") {
                  return (
                    <span key={idx} className="font-anton uppercase text-2xl tracking-widest text-[#0F172A]">
                      {t.text}
                    </span>
                  );
                }
                return (
                  <span key={idx} className="font-anton uppercase text-2xl tracking-widest">
                    {t.text}
                  </span>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="px-4 sm:px-6 max-w-7xl mx-auto w-full py-20 sm:py-32 relative z-10">
        {/* BACKGROUND TYPOGRAPHY (Decorative) */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-[0.04]">
          <span className="font-anton text-[20vw] leading-[0.8] text-[#0F172A] block break-all">
            {bgWords[0]}
            <br />
            {bgWords[1]}
            <br />
            {bgWords[2]}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative">
          {/* LEFT: THE HOOK (Big Statement) */}
          <div className="lg:col-span-5 border-l-8 border-[#0F172A] pl-6 sm:pl-10 py-4">
            <span className="block font-serif italic text-2xl sm:text-3xl text-[#0087E6] mb-2">
              {philosophyKicker}
            </span>

            {/* Keep same design structure, just map strings */}
            <h2 className="font-anton text-[#0F172A] uppercase text-5xl sm:text-7xl leading-[0.9]">
              {pLine0}
              <br />
              {pLine1}
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0F172A] to-[#0087E6]">
                {pLine2}
              </span>
            </h2>
          </div>

          {/* RIGHT: THE CONTENT (Editorial) */}
          <div className="lg:col-span-7 flex flex-col gap-10">
            {/* The Description */}
            <div className="bg-[#F8FAFC]/90 backdrop-blur-sm p-8 border border-gray-100 shadow-sm relative group">
              <Quote
                className="absolute top-4 right-4 text-[#0087E6]/20 group-hover:text-[#0087E6] transition-colors duration-500"
                size={60}
              />

              <p className="font-manrope text-lg sm:text-xl font-medium text-[#0F172A] leading-relaxed mb-6">
                {pizza.description}
              </p>

              <div className="w-12 h-1 bg-[#0087E6] mb-6" />

              <p className="font-serif italic text-gray-500">"{kitchenQuote}"</p>
            </div>

            {/* Sensory Breakdown (Minimalist Grid) */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 border border-[#0F172A]/10 hover:border-[#0F172A] transition-colors cursor-crosshair bg-white">
                <span className="block text-[10px] uppercase font-bold text-[#0087E6] mb-1">Texture</span>
                <span className="font-anton text-xl text-[#0F172A] uppercase">{sensoryTexture}</span>
              </div>
              <div className="text-center p-4 border border-[#0F172A]/10 hover:border-[#0F172A] transition-colors cursor-crosshair bg-white">
                <span className="block text-[10px] uppercase font-bold text-[#0087E6] mb-1">Profile</span>
                <span className="font-anton text-xl text-[#0F172A] uppercase">{sensoryProfile}</span>
              </div>
              <div className="text-center p-4 border border-[#0F172A]/10 hover:border-[#0F172A] transition-colors cursor-crosshair bg-white">
                <span className="block text-[10px] uppercase font-bold text-[#0087E6] mb-1">Finish</span>
                <span className="font-anton text-xl text-[#0F172A] uppercase">{sensoryFinish}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Marquee Animation Style */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
      `}</style>
    </section>
  );
};

/* --- 3. THE SQUAD (Ingredients) --- */
const TheSquad = ({ pizza }) => {
  const ingredients = Array.isArray(pizza.ingredients) ? pizza.ingredients : [];

  return (
    <section className="py-12 sm:py-16 md:py-24 bg-[#F8FAFC] border-t border-gray-100">
      <div className="px-4 sm:px-6 max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-12">
          <span className="text-[#0087E6] font-bold text-[10px] uppercase tracking-widest mb-2 block">
            The Formation
          </span>
          <h2 className="font-anton text-[#0F172A] uppercase" style={{ fontSize: "clamp(1.5rem, 5vw, 3rem)" }}>
            Selected Ingredients
          </h2>
        </div>

        <div className="grid grid-cols-1 min-[400px]:grid-cols-2 lg:grid-cols-4 gap-4">
          {ingredients.map((ing, i) => (
            <div
              key={i}
              className="bg-white p-5 sm:p-6 rounded-xl shadow-sm border border-gray-100 active:scale-[0.98] transition-transform"
            >
              <div className="w-10 h-10 bg-[#0087E6]/10 text-[#0087E6] rounded-full flex items-center justify-center mb-4">
                <span className="font-anton text-lg">{i + 1}</span>
              </div>
              <h3 className="font-anton text-lg text-[#0F172A] uppercase mb-1">{ing.name}</h3>
              <div className="text-[9px] font-bold uppercase tracking-wider text-[#0087E6]/80 mb-2">{ing.role}</div>
              <p className="text-sm text-gray-500 leading-relaxed border-t border-gray-100 pt-3">{ing.desc}</p>
            </div>
          ))}

          {!ingredients.length && (
            <div className="col-span-full text-center text-sm text-gray-500">Ingredients will be added soon.</div>
          )}
        </div>
      </div>
    </section>
  );
};

/* --- 4. PAIRING (The Assist) --- */
const TheAssist = ({ pizza }) => {
  return (
    <section className="py-10 sm:py-16 md:py-20 bg-white">
      <div className="px-4 sm:px-6 max-w-7xl mx-auto">
        <div className="bg-[#0087E6] rounded-2xl p-5 sm:p-8 md:p-10 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

          <div className="relative z-10 flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
            <div className="bg-white/20 p-3 rounded-full flex-shrink-0">
              <Wine size={24} />
            </div>
            <div>
              <h3 className="font-anton text-xl sm:text-2xl md:text-3xl uppercase mb-2">The Perfect Assist</h3>
              <p className="text-white/80 text-sm sm:text-base mb-2">We recommend pairing this pizza with:</p>
              <p className="font-serif italic text-lg sm:text-xl md:text-2xl text-[#FFD700]">
                {pizza.pairing || "A crisp Peroni or a light Chianti"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* --- 5. THE LEGACY (EDITORIAL STYLE) --- */
const TheLegacy = ({ pizza }) => {
  const jerseyColor = pizza.jerseyColor || "#0087E6";
  const jerseyNumber = pizza.jerseyNumber || "10";
  const jerseyTextColor = pizza.jerseyTextColor || "#FFFFFF";
  const jerseyName =
    pizza.jerseyName ||
    pizza.playerInspiration?.toUpperCase() ||
    pizza.name?.split("'")?.[0]?.toUpperCase() ||
    "D10";

  return (
    <section className="relative bg-[#0F172A] overflow-hidden py-12 sm:py-16 md:py-24">
      {/* BIG NUMBER WATERMARK */}
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/4 font-anton select-none pointer-events-none"
        style={{
          fontSize: "clamp(10rem, 40vw, 45rem)",
          color: jerseyColor,
          opacity: 0.08,
          lineHeight: 0.8,
        }}
      >
        {jerseyNumber}
      </div>

      <div className="px-4 sm:px-6 max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-white/20 rounded-full mb-4 sm:mb-6">
              <Crown size={12} className="text-[#FFD700]" />
              <span className="text-white/80 font-bold uppercase tracking-wider text-[9px]">
                Icon Series
              </span>
            </div>

            <h2
              className="font-anton text-white uppercase leading-[0.9] mb-4 sm:mb-6"
              style={{ fontSize: "clamp(2rem, 8vw, 5rem)" }}
            >
              {pizza.playerInspiration || "The Legend"}
            </h2>

            {/* ✅ JERSEY STRIP (minimal add, uses your data) */}
            <div className="flex flex-wrap items-center gap-3 mb-4 sm:mb-6">
              <div
                className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-white/10"
                style={{ background: `${jerseyColor}22` }}
              >
                <span
                  className="font-anton text-lg"
                  style={{ color: jerseyTextColor }}
                >
                  #{jerseyNumber}
                </span>
                <div className="h-4 w-px bg-white/15" />
                <span
                  className="font-anton text-sm tracking-widest"
                  style={{ color: jerseyTextColor }}
                >
                  {jerseyName}
                </span>
              </div>

              <div className="text-white/40 text-[10px] uppercase tracking-wider">
                "{pizza.nickname}"
              </div>
            </div>

            <p className="text-white/60 text-sm sm:text-base leading-relaxed max-w-md">
              {pizza.playerBio ||
                "This pizza is a tribute to a career built on passion, grit, and excellence."}
            </p>
          </div>

          <div className="relative">
            <Quote
              className="absolute -top-4 -left-2 sm:-top-6 sm:-left-4 w-10 h-10 sm:w-14 sm:h-14"
              style={{ color: jerseyColor }}
            />
            <p
              className="font-serif italic text-white leading-snug pl-6 sm:pl-8"
              style={{ fontSize: "clamp(1.25rem, 4vw, 2.25rem)" }}
            >
              "{pizza.quote?.text || "To be the best, you must handle the heat."}"
            </p>

            {/* ✅ quote author (optional, small, no redesign) */}
            {pizza.quote?.author && (
              <div className="mt-3 pl-6 sm:pl-8 text-white/50 text-sm font-manrope">
                — {pizza.quote.author}
              </div>
            )}

            <div className="mt-4 sm:mt-6 flex items-center gap-3 pl-6 sm:pl-8">
              <div className="h-0.5 w-8" style={{ backgroundColor: jerseyColor }} />
              <span className="font-anton text-base sm:text-lg text-white uppercase tracking-wide">
                {pizza.name?.split(" ")?.[0] || "D10"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};


/* --- 6. SCOUT REPORT (Related Items) - IMPROVED GRID SHOWING ALL PIZZAS --- */
const ScoutReport = ({ currentId }) => {
  const related = getRelatedPizzas(currentId);
  if (!related?.length) return null;

  return (
    <section className="py-12 sm:py-16 md:py-24 bg-white">
      <div className="px-4 sm:px-6 max-w-7xl mx-auto">
        <div className="mb-6 sm:mb-10">
          <span className="text-[#0087E6] font-bold text-[10px] uppercase tracking-widest mb-2 block">
            Transfer Market
          </span>
          <h2 className="font-anton text-[#0F172A] uppercase" style={{ fontSize: "clamp(1.5rem, 5vw, 3rem)" }}>
            Other Legends
          </h2>
          <p className="text-gray-500 text-sm mt-2 font-manrope">
            Explore our full roster of {related.length} signature pizzas
          </p>
        </div>

        {/* IMPROVED RESPONSIVE GRID SYSTEM */}
        <div 
          className="grid gap-4 sm:gap-6 lg:gap-8"
          style={{
            gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 280px), 1fr))"
          }}
        >
          {related.map((item, index) => (
            <Link 
              key={item.id} 
              to={`/menu/${item.slug}`} 
              className="group block active:opacity-80"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-gray-100 rounded-lg mb-4">
                <img
                  src={item.heroImage || item.detailImage || item.image}
                  alt={item.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                  <span className="px-4 py-2 bg-white text-[#0087E6] font-bold uppercase tracking-wider text-[10px] rounded-full transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    View Profile
                  </span>
                </div>
                {/* Badge for index */}
                <div className="absolute top-3 left-3 w-8 h-8 bg-[#0087E6] text-white rounded-full flex items-center justify-center">
                  <span className="font-anton text-sm">{index + 1}</span>
                </div>
              </div>
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0 flex-1">
                  <h3
                    className="font-anton text-[#0F172A] uppercase leading-tight mb-1 group-hover:text-[#0087E6] transition-colors truncate"
                    style={{ fontSize: "clamp(1rem, 2.5vw, 1.5rem)" }}
                  >
                    {item.name}
                  </h3>
                  <p className="font-serif italic text-gray-500 text-sm truncate">"{item.nickname}"</p>
                </div>
                <span className="font-anton text-base sm:text-lg text-gray-300 flex-shrink-0">{item.price}</span>
              </div>
            </Link>
          ))}
        </div>

        {/* Show count indicator */}
        <div className="mt-8 sm:mt-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F8FAFC] rounded-full border border-gray-100">
            <span className="font-manrope text-sm text-gray-500">
              Showing all <span className="font-bold text-[#0F172A]">{related.length}</span> legends
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

/* --- 7. CTA SECTION --- */
const TransferMarketCTA = () => {
  return (
    <section className="bg-[#F8FAFC] py-12 sm:py-16 md:py-20 text-center border-t border-gray-100">
      <div className="px-4 sm:px-6 max-w-7xl mx-auto">
        <h2 className="font-anton text-[#0F172A] uppercase mb-6 sm:mb-8" style={{ fontSize: "clamp(1.5rem, 4vw, 2.5rem)" }}>
          Still Hungry?
        </h2>

        <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 max-w-md sm:max-w-none mx-auto">
          <Link
            to="/menu"
            className="flex items-center justify-center gap-2 px-6 py-3 bg-[#0F172A] text-white font-bold uppercase tracking-wider text-xs sm:text-sm active:bg-[#0087E6] sm:hover:bg-[#0087E6] transition-colors"
          >
            View Full Menu <ArrowRight size={16} />
          </Link>

            <a
            href={D10.googleMaps}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#0F172A] text-[#0F172A] font-bold uppercase tracking-wider text-xs sm:text-sm active:bg-gray-100 sm:hover:bg-white sm:hover:shadow-md transition-all"
          >
            <MapPin size={16} /> Visit Us
          </a>
        </div>
      </div>
    </section>
  );
};

/* --- JSON-LD builder (dynamic per pizza) --- */
const buildJsonLd = ({ pizza, canonical }) => {
  const img = pizza?.heroImage || pizza?.detailImage || pizza?.image;
  const ingredientsList = Array.isArray(pizza?.ingredients)
    ? pizza.ingredients.map((i) => i.name).filter(Boolean)
    : [];

  return {
    "@context": "https://schema.org",
    "@type": "MenuItem",
    name: pizza?.name,
    description: pizza?.shortDescription || pizza?.description || "",
    image: img ? [img] : undefined,
    offers: pizza?.price
      ? {
          "@type": "Offer",
          priceCurrency: "EUR",
          price: String(pizza.price).replace(/[^\d.,]/g, "").replace(",", ".") || undefined,
          url: canonical,
          availability: "https://schema.org/InStock",
        }
      : undefined,
    menuAddOn: ingredientsList.length ? ingredientsList.map((n) => ({ "@type": "MenuItem", name: n })) : undefined,
    provider: {
      "@type": "Restaurant",
      name: SITE.name,
      url: SITE.baseUrl,
    },
    url: canonical,
  };
};

/* --- MAIN PAGE COMPONENT --- */
export default function ItemPage() {
  const { slug } = useParams();
  const location = useLocation();

  const pizza = useMemo(() => getPizzaBySlug(slug), [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (!pizza) return <Navigate to="/menu" replace />;

  const canonical = `${SITE.baseUrl}/menu/${pizza.slug}`;
  const title = `${pizza.name} (${pizza.nickname}) | Menu | ${SITE.name}`;
  const description =
    pizza.shortDescription ||
    pizza.excerpt ||
    (typeof pizza.description === "string" ? pizza.description.slice(0, 155) : "") ||
    "Explore this pizza profile: ingredients, pairing, and story.";

  const image = pizza.ogImage || pizza.heroImage || pizza.detailImage || pizza.image || SITE.ogDefault;

  const jsonLd = buildJsonLd({ pizza, canonical });

  return (
    <div className="bg-[#0F172A] min-h-screen w-full overflow-x-hidden selection:bg-[#0087E6] selection:text-white">
      <SEO title={title} description={description} image={image} canonical={canonical} type="article" jsonLd={jsonLd} />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Manrope:wght@400;500;700;800&family=Playfair+Display:ital,wght@0,400;1,400;1,700&display=swap');
        .font-anton { font-family: 'Anton', sans-serif; }
        .font-manrope { font-family: 'Manrope', sans-serif; }
        .font-serif { font-family: 'Playfair Display', serif; }

        html {
          scroll-behavior: smooth;
          -webkit-text-size-adjust: 100%;
        }
        body { overflow-x: hidden; }
        @media (pointer: coarse) { a, button { min-height: 44px; } }
        * { -webkit-tap-highlight-color: transparent; }
      `}</style>

      <Navbar />

      <main className="w-full">
        <ProductHero pizza={pizza} />
        <TheDish pizza={pizza} />
        <TheSquad pizza={pizza} />
        <TheAssist pizza={pizza} />
        <TheLegacy pizza={pizza} />
        <ScoutReport currentId={pizza.id} />
        <TransferMarketCTA />
      </main>

      <Footer />
    </div>
  );
}